<template>
  <div>
    <h1 class="mb-30">Материалы</h1>
    <VButton
      class="mb-30"
      size="menu"
      icon="filter"
      color="primary-sm"
      :active="isFilter"
      @click.native="openFilter"
    />

    <transition name="fade">
      <MaterialFilter class="mb-30" v-if="isFilter && !isMobile" @loadList="loadList" />
    </transition>
    <BaseModal
      v-if="isModal === 'filter'"
      class="modal--sm modal--filter"
      :hidden-close-btn="true"
      @close="closeModal"
    >
      <MaterialFilter in-modal @closeModal="closeModal"  @loadList="loadList" />
    </BaseModal>

    <div>
      <Spinner v-if="isLoading"/>

      <AppTable
        v-if="materialsLength && !isLoading"
        class="table--materials"
        :columns="columns"
        :items="materials.items"
        is-tree-table
        :tree-children-keys="treeChildrenKeys"
        :loading="isLoading"
        :is-open-tree="false"
        is-sticky
      >
        <!-- Кастомный рендер ячеек: для файлов используем AttachmentFilesList -->
        <template #cell="{ item, col, lvl }">
          <span>
            <template v-if="col.name === 'name'">
              <template v-if="lvl === 2">
                {{ item.d_wire }} мм
              </template>
              <template v-else-if="lvl >= 3">
                <!-- Если есть account_number — это заказ -->
                <template v-if="'account_number' in item">
                  <router-link
                    class="link"
                    target="_blank"
                    :to="{ name: 'OrderDetails', params: { id: item.order_id } }"
                  >
                    {{ !isMobile ? 'Заказ ' : '' }}{{ item.account_number }}
                  </router-link>
                </template>
                <!-- Если есть length — это длина -->
                <template v-else-if="'length' in item">
                  {{ item.length }}
                </template>
                <!-- fallback -->
                <template v-else>
                  {{ item.name }}
                </template>
              </template>
              <template v-else>
                {{ item.name }}
              </template>
            </template>
                    <!-- Вторая колонка: "Свободный остаток" -->
            <template v-else-if="col.name === 'free_balance'">
              <span :class="{ 'color--red': isFreeAmountLow(item) }">
                {{ calcFreeAmount(item) }}
              </span>
            </template>
            <template v-else>
              {{ getItemValue(item, col.name) }}
            </template>
          </span>
        </template>
      </AppTable>

      <h2 v-if="!materialsLength && !isLoading">
        Ничего не найдено
      </h2>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import AppTable from '@/components/ui/AppTable.vue'
import VButton from '@/components/simple/button/VButton.vue'
import { mixinIsFilter } from '@/mixins/mixinIsFilter'
import BaseModal from '@/components/ui/Modal/BaseModal.vue'
import { mixinModal } from '@/mixins/modal/mixinModal'
import MaterialFilter from '@/components/warehouse/material/MaterialFilter.vue'
import { fixedNumberCopy } from '@/utils/number'
import { toNum } from '@/utils/string'
import { calcFreeAmount } from '@/utils/warehouse/material'

export default {
  name: 'Warehouse',
  components: { MaterialFilter, BaseModal, VButton, AppTable },
  mixins: [mixinModal, mixinIsFilter],
  data () {
    return {
      showStage: true,
      treeChildrenKeys: {
        // Для обратной совместимости
        category: 'd_wires',
        categoryItem: 'lengths',
        // Новая структура для многоуровневой вложенности
        levelKeys: {
          1: ['d_wires'], // Второй уровень
          2: ['lengths', 'orders'], // Третий уровень - может быть lengths или orders
          3: ['orders'] // Четвертый уровень - только если был lengths
        }
      },
      columns: [
        {
          title: 'd проволоки',
          name: 'name'
        },
        {
          title: 'Свободный остаток',
          name: 'free_balance'
        },
        {
          title: 'В резерве на запуск, КГ',
          name: 'weight_reserved_start'
        },
        {
          title: 'В производстве в ERP, КГ',
          name: 'weight_reserved'
        },
        {
          title: 'Всего на складах в 1C',
          name: 'weight'
        }
      ]
    }
  },

  mounted () {
    this.getMaterialsTree()
    this.getMaterialsFilters()
  },

  beforeDestroy () {
    this.RESET_FILTER()
  },

  computed: {
    ...mapGetters('warehouse', {
      materials: 'material/materials',
      isLoading: 'material/isLoading'
    }),
    ...mapState('mobile', {
      isMobile: 'isMobile'
    }),

    materialsLength () {
      return this.materials?.items?.length
    }
  },

  methods: {
    ...mapActions('warehouse', {
      getMaterialsTree: 'material/getMaterialsTree',
      getMaterialsFilters: 'material/getMaterialsFilters'
    }),
    ...mapMutations('warehouse', {
      RESET_FILTER: 'material/RESET_FILTER'
    }),

    loadList () {
      this.getMaterialsTree()
    },

    // Рассчитываем свободный остаток по формуле (weight - (reserved + reservedStart))
    calcFreeAmount (item) {
      if (
        item.weight === undefined ||
        item.weight_reserved === undefined ||
        item.weight_reserved_start === undefined
      ) return null

      return calcFreeAmount(item)
    },

    // Проверяем что свободный остаток меньше минимума
    isFreeAmountLow (item) {
      const free = this.calcFreeAmount(item)
      const min = toNum(item.weight_min) || 0
      return free < min
    },

    // Для веса ограничиваем количество чисел до запятой до 3
    getItemValue (item, name) {
      if (['weight', 'weight_reserved', 'weight_reserved_start'].includes(name)) {
        return (item[name] || item[name] === 0) ? fixedNumberCopy(item[name], 3) : ''
      }
      return item[name]
    }
  }
}
</script>
